import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import compose from '../utils/compose';
import withLogger from '../utils/withLogger';
import withNuiFormatter from '../utils/withNuiFormatter';
import withNuiIntl from '../utils/withNuiIntl';
import withStylesLoaded from '../utils/withStylesLoaded';
import isFiori from '../utils/isFiori';
import GatewayHeader from './_GatewayHeader';
import InlineError from '../FallbackComponent/_InlineError';
import Shellbar from './_Shellbar';
import { SERVICE_DESCRIPTION_GUIDE_ID, SERVICE_DESCRIPTION_GUIDE_URL } from '../constants';

const getProfileMenuItem = ({ formatter, isLoggedIn }) => (isLoggedIn ? {
    name: formatter.formattedMessage({ id: 'CoreUI.profile.profile' }),
    id: 'profile',
    url: '#', // too many variables to try and determine the proper profile settings page here
} : undefined
);

const getHelpMenuItem = ({ formatter, menuData }) => {
    const adjustedHelpMenuItem = menuData?.helpMenuItem || {
        name: formatter.formattedMessage({ id: 'CoreUI.help' }),
        id: 'help',
        url: '/',
    };

    // due to how language bundles load, we have have gotten the menu item, but
    // not gotten the text so let's go back and update that
    adjustedHelpMenuItem.name = formatter.formattedMessage({ id: 'CoreUI.help' });

    const helpSubMenuItems = adjustedHelpMenuItem.subItems || [];

    // add Service Description Guide if not present
    const sdgMenuItem = helpSubMenuItems.find((item) => item.id === SERVICE_DESCRIPTION_GUIDE_ID);

    // due to how language bundles load, we have have gotten the menu item, but
    // not gotten the text so let's go back and update that
    if (sdgMenuItem) {
        sdgMenuItem.name = formatter.formattedMessage({ id: 'CoreUI.serviceDescriptionGuide' });
    } else {
        helpSubMenuItems.push({
            name: formatter.formattedMessage({ id: 'CoreUI.serviceDescriptionGuide' }),
            id: SERVICE_DESCRIPTION_GUIDE_ID,
            url: SERVICE_DESCRIPTION_GUIDE_URL,
            target: '_blank',
        });
    }

    adjustedHelpMenuItem.subItems = helpSubMenuItems;

    return adjustedHelpMenuItem;
};

const getMinimalMenuData = ({ formatter, isLoggedIn, logger }) => {
    const correlationId = uuidv4();
    if (isLoggedIn) logger.error('Menu data is missing', { correlationId });

    return {
        helpMenuItem: getHelpMenuItem({ formatter }),
        homeMenuItem: {
            name: formatter.formattedMessage({ id: 'CoreUI.home' }),
            id: 'home',
            url: isLoggedIn ? '/home.asp' : undefined,
        },
        profileMenuItem: getProfileMenuItem({ formatter, isLoggedIn }),
        // mark this so we know this is "fake" menu data
        noMenuDataFallback: isLoggedIn ? (
            <InlineError correlationId={correlationId} />
        ) : undefined,
    };
};

const isMayFourth = () => {
    // May Fourth Easter Egg
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentDay = currentDate.getDate();

    // currentMonth is 0 indexed (Jan === 0)
    return currentMonth === 4 && currentDay === 4;
};

const AppHeader = (props) => {
    const {
        menuData,
        ...otherProps
    } = props;

    // This is a patch for nui apps that rely on Menu.cshtml urls like
    // eslint-disable-next-line
    // javascript:if (CNQR && CNQR.currentPage && (CNQR.currentPage === 'processor') && CNQR.processor && CNQR.processor.MenuLinkClicked) {CNQR.processor.MenuLinkClicked()} else {document.location.href='?RptListFilter=processor'}
    // which Error if CNQR is not defined
    // TODO: It'd be better to do these checks here:
    // https://github.concur.com/t1/travel/blob/master/Outtask%20Web%20Server/SharedMasterPages/Gateway/Menu.cshtml
    if (typeof CNQR === 'undefined') {
        window.CNQR = {};
    }

    if (menuData && !menuData.profileMenuItem) {
        menuData.profileMenuItem = getProfileMenuItem(props);
    }

    if (menuData) {
        menuData.helpMenuItem = getHelpMenuItem(props);
    }

    const adjustedProps = {
        ...otherProps,
        isMayFourth: isMayFourth(),
        menuData: menuData || getMinimalMenuData(props),
    };

    if (isFiori(props.brandingId, props.themeName)) {
        return (<Shellbar {...adjustedProps} />);
    }

    return (<GatewayHeader {...adjustedProps} />);
};

AppHeader.displayName = 'AppHeader';

AppHeader.propTypes = {
    ...GatewayHeader.propTypes,
};

export default compose(
    withStylesLoaded,
    withNuiIntl,
    withNuiFormatter,
    withLogger,
)(AppHeader);
